/** Lib Imports */
import PropTypes from "prop-types";
import Chart from "react-apexcharts";

/** Utilities */
import { Card } from "primereact/card";

/** Enums & Constants */
import { STATUS_COLOR_MAP } from "../../../enums/ApplicantStatus.ENUM";
import { checkHasData, renderChartLoader, renderNoData, resolveChartColors } from "../../../utils/common/chartHelper";

/** CSS */
import "./ChartWrapper.scss";

const ChartWrapper = ({
  title,
  type = "bar",
  data = [],
  xKey,
  yKey,
  seriesConfig,
  height = 320,
  colorType = null,
  loading = false,
  noDataMessage = "No data available",
  customOptions = {},
  centerLabel = "Total"
}) => {
  // data transformation
  const buildSeries = () => {
    if (!data || data.length === 0) return { series: [], categories: [] };

    // pie / donut
    if (type === "pie" || type === "donut") {
      return {
        series: data.map(item => item[yKey] || 0),
        labels: data.map((item, i) => item[xKey] || `Item ${i + 1}`)
      };
    }

    // multi series
    if (seriesConfig?.length) {
      const categories = data.map(item => item[xKey] || "");
      const series = seriesConfig.map(s => ({
        name: s.name,
        data: data.map(item => item[s.key] || 0)
      }));
      return { series, categories };
    }

    // auto stacked
    if (data[0]?.statuses) {
      const categories = data.map(item => item[xKey] || "");

      const allKeys = [
        ...new Set(data.flatMap(item => Object.keys(item.statuses || {})))
      ];

      const series = allKeys.map(key => ({
        name: key,
        data: data.map(item => item.statuses?.[key] || 0)
      }));

      return { series, categories };
    }

    // single series
    return {
      categories: data.map(item => item[xKey]),
      series: [
        {
          name: title || "Data",
          data: data.map(item => item[yKey] || 0)
        }
      ]
    };
  };

  const { series, categories, labels } = buildSeries();

  // color handling
  const colors = resolveChartColors({
    colorType,
    data,
    series,
    xKey,
    STATUS_COLOR_MAP
  });

  // chart options
  const buildOptions = () => {
    const base = {
      chart: {
        type,
        toolbar: { show: false },
        ...(customOptions.chart || {})
      },
      colors,
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "center",
        ...(customOptions.legend || {})
      }
    };

    // pie / donut
    if (type === "pie" || type === "donut") {
      return {
        ...base,
        labels,
        tooltip: {
          y: { formatter: (val) => val },
          ...(customOptions.tooltip || {})
        },
        plotOptions: {
          pie: {
            ...(type === "donut" && {
              donut: {
                size: "60%",
                labels: {
                  show: true,
                  total: {
                    show: true,
                    label: centerLabel,
                    fontSize: "13px",
                    fontWeight: 500,
                    formatter: (w) =>
                      w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                  }
                }
              }
            })
          },
          ...customOptions.plotOptions
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => `${val.toFixed(1)}%`,
          ...(customOptions.dataLabels || {})
        }
      };
    }

    // axis charts
    return {
      ...base,
      xaxis: {
        categories,
        ...(customOptions.xaxis || {})
      },
      stroke:
        type === "line" || type === "area"
          ? { curve: "smooth", width: 3 }
          : {},
      fill:
        type === "area"
          ? { type: "gradient", opacity: 0.3 }
          : {},
      plotOptions:
        type === "bar"
          ? {
            bar: {
              borderRadius: 6,
              columnWidth: "50%",
              distributed:
                customOptions?.plotOptions?.bar?.distributed || false
            },
            ...(customOptions.plotOptions || {})
          }
          : {},
      tooltip: {
        ...(customOptions.tooltip || {})
      },
      markers: {
        size: 4,
        ...(customOptions.markers || {})
      },
      grid: {
        borderColor: "#f1f1f1",
        ...(customOptions.grid || {})
      },
      dataLabels: {
        enabled: false,
        ...(customOptions.dataLabels || {})
      }
    };
  };

  const options = buildOptions();

  // data validation
  const hasData = checkHasData({ type, series });

  // content rendering
  let chartContent;

  if (loading) {
    chartContent = renderChartLoader();
  } else if (!hasData) {
    chartContent = renderNoData(noDataMessage);
  } else {
    chartContent = (
      <div className="chart-container">
        <Chart
          options={options}
          series={series}
          type={type}
          height={height}
        />
      </div>
    );
  }

  return (
    <Card className="chart-card" title={title}>
      {chartContent}
    </Card>
  );
};

ChartWrapper.propTypes = {
  title: PropTypes.string,
  type: PropTypes.oneOf(["bar", "line", "area", "pie", "donut"]),
  data: PropTypes.arrayOf(PropTypes.object),
  xKey: PropTypes.string,
  yKey: PropTypes.string,
  seriesConfig: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired
    })
  ),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  colorType: PropTypes.string,
  loading: PropTypes.bool,
  noDataMessage: PropTypes.string,
  customOptions: PropTypes.object,
  centerLabel: PropTypes.string
};

export default ChartWrapper;